// Keyboard focus trap for modal dialogs. Keeps Tab / Shift+Tab cycling
// inside the ref'd container while active, auto-focuses the first
// interactive element on open, and restores focus to whatever had it
// before activation when the dialog closes.
//
// Not suitable for persistent drawers — those need to release focus back
// to the surrounding list. Use on components with role="dialog".
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  active: boolean,
): void {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const container = containerRef.current;
    if (!container) return;

    const focusables = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(el => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);

    // Move focus inside unless it's already there (e.g. autoFocus handled it).
    if (!container.contains(document.activeElement)) {
      const first = focusables()[0];
      if (first) first.focus();
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    return () => {
      container.removeEventListener('keydown', handler);
      const ret = returnFocusRef.current;
      if (ret && typeof ret.focus === 'function') {
        try {
          ret.focus();
        } catch {
          // Ref target may have been unmounted; silent fallback — focus
          // stays on <body> which is the browser default behavior anyway.
        }
      }
    };
  }, [active, containerRef]);
}
